import React, { useState, useEffect, useCallback, memo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { CORK_BOARD_INPUT_LIMITS } from '@/constants/residents/corkBoardConstants';
import { AdminDialogModal } from '@/components/common/admin/mobile/AdminDialogModal';
import { GlassFormInput } from '@/components/admin/redesign';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { TagColorPicker } from './TagColorPicker';
import { TagPill } from './TagPill';
import { TagIconPicker } from './TagIconPicker';
import type { CorkBoardTag } from '@/types/residents/corkBoard';

interface TagFormModalProps {
  visible: boolean;
  tag: CorkBoardTag | null;
  isSaving: boolean;
  onSave: (data: { name: string; color: string | null; icon: string | null }) => void;
  onClose: () => void;
}

export const TagFormModal: React.FC<TagFormModalProps> = memo(
  ({ visible, tag, isSaving, onSave, onClose }) => {
    const { redesign } = useAdminColors();
    const isEditing = !!tag;
    const [name, setName] = useState('');
    const [color, setColor] = useState<string | null>(null);
    const [icon, setIcon] = useState<string | null>(null);
    const [nameError, setNameError] = useState('');

    useEffect(() => {
      if (visible) {
        setName(tag?.name ?? '');
        setColor(tag?.color ?? null);
        setIcon(tag?.icon ?? null);
        setNameError('');
      }
    }, [visible, tag]);

    const validate = useCallback((): boolean => {
      const trimmed = name.trim();
      if (!trimmed) {
        setNameError('Tag name is required');
        return false;
      }
      if (trimmed.length > CORK_BOARD_INPUT_LIMITS.tagNameMax) {
        setNameError(`Name must be ${CORK_BOARD_INPUT_LIMITS.tagNameMax} characters or fewer`);
        return false;
      }
      setNameError('');
      return true;
    }, [name]);

    const handleSave = useCallback(() => {
      if (isSaving || !validate()) return;
      onSave({
        name: name.trim(),
        color: color ?? null,
        icon: icon ?? null,
      });
    }, [isSaving, validate, onSave, name, color, icon]);

    return (
      <AdminDialogModal
        visible={visible}
        title={isEditing ? 'Edit Tag' : 'New Tag'}
        onClose={onClose}
        onSave={handleSave}
        isSaving={isSaving}
      >
        <GlassFormInput
          label="Name"
          required
          value={name}
          onChangeText={(text) => {
            setName(text);
            if (nameError) setNameError('');
          }}
          placeholder="e.g. Events, For Sale, Help Needed"
          maxLength={CORK_BOARD_INPUT_LIMITS.tagNameMax}
          error={nameError || undefined}
          autoFocus
        />

        <TagColorPicker selectedColor={color} onSelectColor={setColor} />

        <TagIconPicker selectedIcon={icon} onSelectIcon={setIcon} />

        {(name.trim() || color || icon) && (
          <View style={styles.previewSection}>
            <Text style={[styles.label, { color: redesign.text.secondary }]}>Preview</Text>
            <View style={styles.previewRow}>
              <TagPill name={name.trim() || 'Tag Name'} color={color} icon={icon} />
            </View>
          </View>
        )}
      </AdminDialogModal>
    );
  },
);

TagFormModal.displayName = 'TagFormModal';


const styles = StyleSheet.create({
  label: {
    fontSize: 13,
    fontWeight: '500',
  },
  previewSection: {
    gap: 8,
    paddingTop: 4,
  },
  previewRow: {
    flexDirection: 'row',
  },
});
